import { apiFetch, ApiError } from './client';
import {
  Author,
  ArchiveItem,
  CommentView,
  CreateCommentRequest,
  CreatePostRequest,
  FolderCount,
  HotPost,
  PageContent,
  PostDetail,
  PostSummary,
  PreviewRequest,
  PreviewResponse,
  SaveDraftRequest,
  TagCount,
  UpdatePostRequest,
} from '../types';

const API_URL = import.meta.env.VITE_API_URL || '/api';

async function fetcher<T>(path: string, options?: RequestInit): Promise<T> {
  return apiFetch(`${API_URL}${path}`, options);
}

export const api = {
  // Public
  health: () => fetcher<string>('/health'),
  listPosts: (params?: { tag?: string; q?: string; folder?: string; page?: number }) => {
    const search = new URLSearchParams();
    if (params?.tag) search.set('tag', params.tag);
    if (params?.q) search.set('q', params.q);
    if (params?.folder) search.set('folder', params.folder);
    if (params?.page) search.set('page', String(params.page));
    return fetcher<PostSummary[]>(`/posts?${search.toString()}`);
  },
  getPost: (slug: string) => fetcher<PostDetail>(`/posts/${slug}`),
  listComments: (slug: string) => fetcher<CommentView[]>(`/posts/${slug}/comments`),
  listTags: () => fetcher<TagCount[]>('/tags'),
  listFolders: () => fetcher<FolderCount[]>('/folders'),
  listHotPosts: () => fetcher<HotPost[]>('/posts/hot'),
  getArchive: () => fetcher<ArchiveItem[]>('/archive'),
  getPage: (slug: string) => fetcher<PageContent>(`/pages/${slug}`),
  savePage: (slug: string, content_md: string) =>
    fetcher<{ ok: boolean }>(`/admin/pages/${slug}`, { method: 'PUT', body: JSON.stringify({ content_md }) }),
  uploadImage: async (file: File): Promise<{ url: string }> => {
    const form = new FormData();
    form.append('file', file);
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/admin/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (!res.ok) {
      let message = `HTTP ${res.status}`;
      try {
        const data = await res.json();
        if (data.error) message = data.error;
      } catch {
        // ignore
      }
      throw new ApiError(message, res.status);
    }
    return res.json();
  },
  getRss: () => fetch(`${API_URL}/rss.xml`).then((r) => r.text()),

  // Auth
  getMe: () => fetcher<{ role: string; user?: Author }>('/auth/me'),

  // User
  createComment: (slug: string, body: CreateCommentRequest) =>
    fetcher<CommentView>(`/posts/${slug}/comments`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Admin
  adminListPosts: () => fetcher<PostSummary[]>('/admin/posts'),
  getAdminPost: (id: string) => fetcher<PostDetail>(`/admin/posts/${id}`),
  createPost: (body: CreatePostRequest) => fetcher<PostDetail>('/admin/posts', { method: 'POST', body: JSON.stringify(body) }),
  updatePost: (id: string, body: UpdatePostRequest) =>
    fetcher<PostDetail>(`/admin/posts/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deletePost: (id: string) => fetcher<{ ok: boolean }>(`/admin/posts/${id}`, { method: 'DELETE' }),
  saveDraft: (id: string, body: SaveDraftRequest) =>
    fetcher<{ ok: boolean }>(`/admin/posts/${id}/draft`, { method: 'POST', body: JSON.stringify(body) }),
  getDraft: (id: string) => fetcher<{ content_md: string }>(`/admin/posts/${id}/draft`),
  publishPost: (id: string) => fetcher<PostDetail>(`/admin/posts/${id}/publish`, { method: 'POST' }),
  unpublishPost: (id: string) => fetcher<PostDetail>(`/admin/posts/${id}/unpublish`, { method: 'POST' }),
  preview: (body: PreviewRequest) => fetcher<PreviewResponse>('/admin/preview', { method: 'POST', body: JSON.stringify(body) }),
  deleteComment: (id: string) => fetcher<{ ok: boolean }>(`/admin/comments/${id}`, { method: 'DELETE' }),
};
